import os from "os"
import path from "path"
import fs from "fs-extra"
import { execa } from "execa"
import { z } from "zod"
import { parse as parseJsonc } from "jsonc-parser"
import { input } from "@inquirer/prompts"

import { initOptionsSchema } from "@/src/commands/init"
import select from "@/src/inquirer/select"
import { colors } from "@/src/utils/colors"
import { getPackageManager } from "@/src/utils/get-package-manager"
import { handleError } from "@/src/utils/handle-error"
import { logger } from "@/src/utils/logger"
import { spinner } from "@/src/utils/spinner"

const MONOREPO_FRAMEWORK_URL =
  "https://codeload.github.com/shadcn-ui/ui/tar.gz/main"

export const FRAMEWORKS = {
  next: "next",
  vite: "vite",
  "next-monorepo": "next-monorepo",
} as const

type Framework = keyof typeof FRAMEWORKS
type ProjectOptions = Pick<
  z.infer<typeof initOptionsSchema>,
  "cwd" | "srcDir" | "components" | "framework" | "reactCompiler"
>
type CreateNextOptions = {
  version: string
  cwd: string
  packageManager: string
  srcDir: boolean
  reactCompiler: boolean
}
type CreateViteOptions = {
  cwd: string
  packageManager: string
  projectName: string
}
type CreateMonorepoOptions = {
  packageManager: string
}

/**
 * Creates a themed input prompt with consistent styling
 */
const createThemedInput = (message: string, defaultValue: string = "") => {
  return input({
    message: colors.reset(message),
    default: defaultValue,
    required: true,
    validate: (value: string) =>
      value.length > 128 ? `Name should be less than 128 characters.` : true,
    theme: {
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
  })
}

/**
 * Creates a themed select prompt with consistent styling
 */
const createThemedSelect = async (
  message: string,
  choices: Array<{ name: string; value: string }>
) => {
  const instructions = `\n${colors.gray("  Use arrow-keys ▲▼ / [Return] to submit")}\n`

  return select({
    message: colors.reset(message),
    instructions,
    theme: {
      icon: {
        cursor: colors.cyan("❯"),
      },
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
    choices,
  })
}

/**
 * Creates a new project based on the specified framework
 */
export async function createProject(options: ProjectOptions) {
  const normalizedOptions = {
    srcDir: false,
    reactCompiler: false,
    ...options,
  }

  let framework: Framework =
    normalizedOptions.framework &&
    FRAMEWORKS[normalizedOptions.framework as Framework]
      ? (normalizedOptions.framework as Framework)
      : "next"

  let projectName = "my-app"
  const nextVersion = "latest"

  // Only prompt for framework if not already specified
  if (!normalizedOptions.framework) {
    const frameworkPromptMessage = `The path ${colors.cyan(normalizedOptions.cwd)} does not contain a package.json file. Would you like to start a new project?`

    const selectedFramework = await createThemedSelect(frameworkPromptMessage, [
      { name: "Next.js", value: "next" },
      { name: "Vite + React + TypeScript", value: "vite" },
      // { name: 'Next.js (Monorepo)', value: 'next-monorepo' }
    ])

    framework = selectedFramework as Framework

    // Add spacing after framework selection
    logger.log("")
  }

  // Prompt for project name
  projectName = await createThemedInput(
    "What is your project named?",
    projectName
  )

  // Get the package manager for the project
  const packageManager = await getPackageManager(normalizedOptions.cwd, {
    withFallback: true,
  })

  const projectPath = path.join(normalizedOptions.cwd, projectName)

  // Validate path is writable
  await validateProjectPath(normalizedOptions.cwd, projectName)

  // Create the project based on framework
  if (framework === FRAMEWORKS.next) {
    await createNextProject(projectPath, {
      version: nextVersion,
      cwd: normalizedOptions.cwd,
      packageManager,
      srcDir: !!normalizedOptions.srcDir,
      reactCompiler: !!normalizedOptions.reactCompiler,
    })
  } else if (framework === FRAMEWORKS.vite) {
    await createViteProject(projectPath, {
      cwd: normalizedOptions.cwd,
      packageManager,
      projectName,
    })
  } else if (framework === FRAMEWORKS["next-monorepo"]) {
    await createMonorepoProject(projectPath, {
      packageManager,
    })
  }

  return {
    projectPath,
    projectName,
    framework,
  }
}

/**
 * Validates that the project path is writable and doesn't already exist
 */
async function validateProjectPath(cwd: string, projectName: string) {
  // Check if path is writable
  try {
    await fs.access(cwd, fs.constants.W_OK)
  } catch (error) {
    logger.break()
    logger.error(`The path ${colors.cyan(cwd)} is not writable.`)
    logger.error(
      `It is likely you do not have write permissions for this folder or the path ${colors.cyan(cwd)} does not exist.`
    )
    logger.break()
    process.exit(0)
  }

  // Check if project already exists
  if (fs.existsSync(path.resolve(cwd, projectName, "package.json"))) {
    logger.break()
    logger.error(
      `A project with the name ${colors.cyan(projectName)} already exists.`
    )
    logger.error(`Please choose a different name and try again.`)
    logger.break()
    process.exit(0)
  }
}

/**
 * Creates a new Next.js project
 */
async function createNextProject(
  projectPath: string,
  options: CreateNextOptions
) {
  const createSpinner = spinner(
    "Creating a new Next.js project. This may take a few minutes."
  ).start()

  // Note: pnpm fails here. Fallback to npx with --use-PACKAGE-MANAGER.
  const args = [
    "--tailwind",
    "--eslint",
    "--typescript",
    "--app",
    options.srcDir ? "--src-dir" : "--no-src-dir",
    "--no-import-alias",
    options.reactCompiler ? "--react-compiler" : "--no-react-compiler",
    `--use-${options.packageManager}`,
  ]

  // Add turbopack for Next.js 15+ or canary/latest
  if (
    options.version.startsWith("15") ||
    options.version.startsWith("latest") ||
    options.version.startsWith("canary")
  ) {
    args.push("--turbopack")
  }

  try {
    await execa(
      "npx",
      [`create-next-app@${options.version}`, projectPath, "--silent", ...args],
      { cwd: options.cwd }
    )

    await updateReadmeFile(projectPath)

    createSpinner.stopAndPersist({
      symbol: colors.cyan("✔"),
    })
  } catch (error) {
    createSpinner?.fail("Something went wrong creating a new Next.js project.")
    logger.break()
    logger.error(
      `Something went wrong creating a new Next.js project. Please try again.`
    )
    process.exit(0)
  }
}

/**
 * Creates a new Vite + React + TypeScript project
 */
async function createViteProject(
  projectPath: string,
  options: CreateViteOptions
) {
  const createSpinner = spinner(
    `Creating a new Vite + React + TypeScript project. This may take a few minutes.`
  ).start()

  try {
    await execa(
      "npm",
      [
        "create",
        "vite@latest",
        options.projectName,
        "--",
        "--template",
        "react-ts",
      ],
      { cwd: options.cwd }
    )

    await execa(options.packageManager, ["install"], {
      cwd: projectPath,
    })

    await setupViteTsConfig(projectPath, options.packageManager)

    await updateReadmeFile(projectPath)

    createSpinner.stopAndPersist({
      symbol: colors.cyan("✔"),
    })
  } catch (error) {
    createSpinner?.fail("Something went wrong creating a new Vite project.")
    handleError(error)
  }
}

/**
 * Configures TypeScript and path aliases for a Vite project
 */
async function setupViteTsConfig(projectPath: string, packageManager: string) {
  try {
    await setupTsConfigPathAliases(projectPath)
    await setupViteConfigPathAliases(projectPath)
    await installTypesNode(projectPath, packageManager)
  } catch (error) {
    logger.warn(
      "Failed to set up TypeScript path aliases, but project creation succeeded"
    )
  }
}

/**
 * Installs @types/node as a development dependency
 */
async function installTypesNode(projectPath: string, packageManager: string) {
  try {
    const devFlag = packageManager === "npm" ? "--save-dev" : "-D"

    await execa(
      packageManager,
      [packageManager === "npm" ? "install" : "add", devFlag, "@types/node"],
      { cwd: projectPath }
    )
  } catch (error) {
    logger.warn("Failed to install @types/node, but project creation succeeded")
  }
}

/**
 * Sets up TypeScript configuration files with path aliases
 */
async function setupTsConfigPathAliases(projectPath: string) {
  const addAliasToTsConfig = async (tsconfigFile: string) => {
    const tsconfigPath = path.join(projectPath, tsconfigFile)
    if (!(await fs.pathExists(tsconfigPath))) return

    const jsonContent = await fs.readFile(tsconfigPath, "utf-8")
    const jsonObj = parseJsonc(jsonContent)

    jsonObj.compilerOptions = {
      ...jsonObj.compilerOptions,
      baseUrl: ".",
      paths: {
        ...(jsonObj.compilerOptions?.paths || {}),
        "@/*": ["./src/*"],
      },
    }

    const updatedJson = JSON.stringify(jsonObj, null, 2)
    await fs.writeFile(tsconfigPath, updatedJson)
  }

  await addAliasToTsConfig("tsconfig.json")
  await addAliasToTsConfig("tsconfig.app.json")
}

/**
 * Sets up Vite configuration with path aliases
 */
async function setupViteConfigPathAliases(projectPath: string) {
  const viteConfigPath = path.join(projectPath, "vite.config.ts")
  const viteConfigContent = await fs.readFile(viteConfigPath, "utf-8")

  const updatedViteConfig = viteConfigContent
    .replace(
      "import { defineConfig } from 'vite'",
      "import { defineConfig } from 'vite'\nimport path from 'path'"
    )
    .replace(
      "plugins: [react()]",
      `plugins: [react()],
  resolve: {
    alias: {
      '@': path.resolve(__dirname, './src')
    }
  }`
    )

  await fs.writeFile(viteConfigPath, updatedViteConfig)
}

/**
 * Creates a new Next.js monorepo project
 */
async function createMonorepoProject(
  projectPath: string,
  options: CreateMonorepoOptions
) {
  const createSpinner = spinner(
    `Creating a new Next.js monorepo. This may take a few minutes.`
  ).start()

  try {
    await downloadAndExtractFramework(projectPath)

    // Run install
    await execa(options.packageManager, ["install"], {
      cwd: projectPath,
    })

    // Initialize git repository
    await initializeGitRepository(projectPath)

    createSpinner?.stopAndPersist({
      symbol: colors.cyan("✔"),
      text: `Created a new Next.js monorepo at ${colors.cyan(projectPath)}`,
    })
  } catch (error) {
    createSpinner?.fail("Something went wrong creating a new Next.js monorepo.")
    handleError(error)
  }
}

/**
 * Downloads and extracts the monorepo framework
 */
async function downloadAndExtractFramework(projectPath: string) {
  // Create temporary directory
  const frameworkPath = path.join(os.tmpdir(), `tiptap-framework-${Date.now()}`)
  await fs.ensureDir(frameworkPath)

  // Download framework
  const response = await fetch(MONOREPO_FRAMEWORK_URL)
  if (!response.ok) {
    throw new Error(
      "The starter project could not be downloaded. Please check your connection and try again."
    )
  }

  // Write the tar file
  const tarPath = path.resolve(frameworkPath, "framework.tar.gz")
  await fs.writeFile(tarPath, Buffer.from(await response.arrayBuffer()))

  // Extract framework
  await execa("tar", [
    "-xzf",
    tarPath,
    "-C",
    frameworkPath,
    "--strip-components=2",
    "ui-main/templates/monorepo-next",
  ])

  // Move to project path and cleanup
  const extractedPath = path.resolve(frameworkPath, "monorepo-next")
  await fs.move(extractedPath, projectPath)
  await fs.remove(frameworkPath)
}

/**
 * Initializes a git repository in the project directory
 */
async function initializeGitRepository(projectPath: string) {
  const cwd = process.cwd()

  try {
    await execa("git", ["--version"], { cwd: projectPath })
    await execa("git", ["init"], { cwd: projectPath })
    await execa("git", ["add", "-A"], { cwd: projectPath })
    await execa("git", ["commit", "-m", "Initial commit"], {
      cwd: projectPath,
    })
    await execa("cd", [cwd])
  } catch (error) {
    logger.warn(
      "Failed to initialize git repository, but project creation succeeded"
    )
  }
}

const simpleEditorTemplateDocs = `# Simple template

Integrate the Tiptap open source editor with UI components and open source extensions.

The Simple Editor Template is a fully working setup for the Tiptap editor. It includes commonly used open source extensions and UI components, all MIT licensed and ready to customize.

**Quick Links:**
- [View in Full Screen](http://template.tiptap.dev/preview/templates/simple)
- [GitHub Repository](https://github.com/ueberdosis/tiptap-ui-components/tree/main/apps/web/src/components/tiptap-templates/simple)

## Installation

Add the Simple Editor Template to your project using the Tiptap CLI:

\`\`\`bash
npx @tiptap/cli add simple-editor
\`\`\`

## Style

> **Missing styles?** If you haven't added the styles yet, make sure to [follow the style setup guide](/ui-components/getting-started/style) to ensure your editor and components render correctly.

## Usage

After installation, use the SimpleEditor component in your React or Next.js project:

\`\`\`jsx
import { SimpleEditor } from '@/components/tiptap-templates/simple/simple-editor'

export default function App() {
  return <SimpleEditor />
}
\`\`\`

## Features

A fully responsive rich text editor with built-in support for common formatting and layout tools. All components are open source and easy to extend.

- **Responsive design**: Mobile-friendly by default
- **Dark and light mode**: Supported out-of-the-box
- **Formatting**: Bold, Italic, Underline
- **Lists**: Bullet, Ordered, Checkboxes
- **Text alignment**: Left, Center, Right, Justified
- **Headings**: Multiple levels via dropdown
- **Image upload**
- **Link editing:** UI for adding and editing links
- **Undo / Redo:** History management

### Used reference components

#### Hooks

- \`use-mobile\`
- \`use-window-size\`

#### Icons

- \`arrow-left-icon\`
- \`highlighter-icon\`
- \`link-icon\`
- \`moon-star-icon\`
- \`sun-icon\`

#### Extensions

- \`selection-extension\`
- \`link-extension\`
- \`trailing-node-extension\`

#### Lib

- \`tiptap-utils\`

#### UI Components

- \`blockquote-button\`
- \`code-block-button\`
- \`color-highlight-button\`
- \`color-highlight-popover\`
- \`heading-button\`
- \`heading-dropdown-menu\`
- \`image-upload-button\`
- \`link-popover\`
- \`list-button\`
- \`list-dropdown-menu\`
- \`mark-button\`
- \`text-align-button\`
- \`undo-redo-button\`

#### Node Components

- \`code-block-node\`
- \`image-node\`
- \`image-upload-node\`
- \`list-node\`
- \`paragraph-node\`

#### Primitives

- \`button\`
- \`spacer\`
- \`toolbar\`

## License

The Simple Editor Template and all included components are MIT licensed. You're free to use, modify, and extend the code as needed.

## Future compatibility

You can extend this template with additional features as your needs grow.

Paid Tiptap Cloud features will have matching UI components that integrate just as easily! No rework needed.`

const notionLikeEditorDocs = `# Notion-like Editor

A Notion-style Tiptap editor with collaboration, AI, and rich UI components. Fully customizable and easy to integrate.

The **Notion-like Editor Template** is a fully featured block-based editor that replicates the familiar Notion experience. It supports collaboration, AI assistance, emoji, drag & drop, advanced formatting—and it's fully customizable.

## Installation

Install the Notion-like editor with the Tiptap CLI:

\`\`\`bash
npx @tiptap/cli add notion-like-editor
\`\`\`

## Styling

> **Note:** If the editor doesn't look right, make sure you've followed the [style setup guide](/ui-components/getting-started/style).

## Configuration

Before running the app, configure the required constants inside \`tiptap-collab-utils.ts\`. This is necessary to enable features like AI or collaboration.

### Environment Variables

Provide values for the following environment variables:

- \`TIPTAP_COLLAB_DOC_PREFIX\` - Prefix for identifying collaborative documents
- \`TIPTAP_COLLAB_APP_ID\` - Your Document Server App ID
- \`TIPTAP_COLLAB_TOKEN\` - JWT token for accessing Collaboration services
- \`TIPTAP_AI_APP_ID\` - Your AI App ID
- \`TIPTAP_AI_TOKEN\` - JWT token for accessing AI services

The above environment variables should be available in the client-side. Depending on your framework, use the following prefixes to expose them to the client:

- **[Next.js](/ui-components/install/next)**: \`NEXT_PUBLIC_\`. For example, \`NEXT_PUBLIC_TIPTAP_COLLAB_DOC_PREFIX\`.
- **[Vite + React](/ui-components/install/vite)**: \`VITE_\`. For example, \`VITE_TIPTAP_COLLAB_DOC_PREFIX\`.
- **Other frameworks**: Follow the specific rules of your framework, and define the variables in the \`tiptap-collab-utils.ts\` file.

### JWT Authentication

Collaboration and AI features require a valid server-generated JWT token passed to the editor. See the \`fetchCollabToken\` function in \`tiptap-collab-utils.ts\` for an example.

> See the full guide on JWT authentication at [https://tiptap.dev/docs/guides/authentication](https://tiptap.dev/docs/guides/authentication).

> **Important:** To get started quickly, you can use the example JWT tokens from your Tiptap Cloud account and store them in the \`TIPTAP_COLLAB_TOKEN\` and \`TIPTAP_AI_TOKEN\` environment variables. However, example JWT tokens are valid for a short time and should not be used in production. In production, implement an API endpoint that generates JWTs on the server side.

### \`room\` prop

Use the \`room\` prop to distinguish collaborative documents. Each session should use a unique room ID.

## Usage

Import and render the \`NotionEditor\` component in your React app:

\`\`\`tsx
import { NotionEditor } from '@/components/tiptap-templates/notion/notion-like-editor'

export default function App() {
  return <NotionEditor room="my-document-room" placeholder="Start writing..." />
}
\`\`\`

## Features

The template includes all the essentials of a modern Notion-style editor:

- **Real-time collaboration**: Live cursors and user presence
- **AI assistance**: Inline AI tools for writing and editing
- **Responsive design**: Mobile-ready UI with adaptive toolbars
- **Dark/light mode**: Fully themed out of the box
- **Slash commands**: \`/\` menu for quick formatting
- **Floating toolbar**: Context-aware formatting
- **Drag and drop**: Block-level reordering
- **Emoji support**: GitHub-style emoji picker
- **Mentions**: \`@user\` autocomplete
- **Rich formatting**:
  - Bold, italic, underline, strikethrough
  - Highlight and color
  - Superscript / subscript
  - Syntax-highlighted code blocks
- **Block types**:
  - Headings, lists, blockquotes, dividers, math
- **Media support**: Drag & drop image upload
- **Link management**: With inline previews
- **Text alignment**: Left, center, right, justified
- **Undo/Redo**: Full editing history
- **Context menus**: Right-click enhancements

## Component Breakdown

### Hooks

- \`use-mobile\`
- \`use-window-size\`
- \`use-ui-editor-state\`

### Icons

- \`arrow-left-icon\`
- \`chevron-right-icon\`
- \`highlighter-icon\`
- \`link-icon\`
- \`more-vertical-icon\`

### Tiptap Extensions

- \`collaboration\`, \`collaboration-cursor\`
- \`selection-extension\`
- \`link-extension\`
- \`trailing-node-extension\`
- \`ai-extension\`
- \`emoji-extension\`
- \`mention-extension\`
- \`mathematics-extension\`
- \`unique-id-extension\`

### Libs

- \`tiptap-utils\`
- \`tiptap-collab-utils\`

### UI Components

- \`ai-menu\`
- \`blockquote-button\`
- \`code-block-button\`
- \`color-highlight-button\`, \`color-highlight-popover\`
- \`drag-context-menu\`
- \`emoji-dropdown-menu\`
- \`heading-button\`, \`heading-dropdown-menu\`
- \`image-upload-button\`
- \`link-popover\`
- \`list-button\`, \`list-dropdown-menu\`
- \`mark-button\`
- \`mention-dropdown-menu\`
- \`slash-dropdown-menu\`
- \`text-align-button\`
- \`turn-into-dropdown\`
- \`undo-redo-button\`

### Nodes

- \`code-block-node\`
- \`image-node\`, \`image-upload-node\`
- \`list-node\`
- \`paragraph-node\`

### Notion-specific

- \`notion-like-editor-header\`
- \`notion-toolbar-floating\`
- \`mobile-toolbar\`
- \`collaboration-users\`
- \`theme-toggle\`

### Primitives

- \`button\`, \`button-group\`
- \`dropdown-menu\`
- \`separator\`, \`spacer\`, \`toolbar\`

### Contexts

- \`user-context\`
- \`collab-context\`

## Collaboration

To use collaboration:

1. Pass a unique \`room\` ID to \`NotionEditor\`
2. Enable JWT auth for each user session
3. User presence and cursors are handled automatically
4. Operational transformation handles concurrent edits
5. Sync and save are managed out-of-the-box

\`\`\`tsx
<NotionEditor room="team-notes" placeholder="Share your ideas..." />
\`\`\`

## AI Integration

The built-in AI tools let you:

- **Generate content** from prompts
- **Improve** existing text
- **Get smart completions** based on context

> **AI Configuration:** Make sure to configure your AI provider. Check the [AI Generation extension docs](/content-ai/capabilities/generation/overview) for setup steps.

## Extendability

This template is designed to grow with your needs. New Tiptap Cloud features will be seamlessly compatible with the same UI system—no rewrites required.`

async function updateReadmeFile(projectPath: string, templateName?: string) {
  const readmePath = path.join(projectPath, "README.md")

  const templateContent = {
    "simple-editor": simpleEditorTemplateDocs,
    "notion-like-editor": notionLikeEditorDocs,
  } as const

  type TemplateKey = keyof typeof templateContent

  const readmeContent =
    templateName && templateName in templateContent
      ? templateContent[templateName as TemplateKey]
      : ""

  try {
    if (readmeContent.trim()) {
      await fs.writeFile(readmePath, readmeContent, "utf-8")
    }
  } catch {
    /* empty */
  }
}
